import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

import { SidebarAccordionItem } from '@/components/templates/sidebar-accordion-template';
import {
  SelectionsDevice,
  SelectionsDeviceGroup,
} from '@/features/trends/components/selections-sidebar/selections.types';
import SelectionState from '@/features/trends/components/selections-sidebar/state/selections.state';
import { currentChartIdAtom } from '@/features/trends/state/current-chart.state';

/**
 * Hook that manages accordion expansion behavior during search operations.
 *
 * ## Features
 * - **Auto-expands** all matching accordion sections when user searches
 * - **Preserves** user's manual expansion state before search begins
 * - **Restores** original expansion state when search is cleared
 * - **Allows** manual collapse/expand during active search without interference
 * - **Per-chart isolation** - each chart maintains its own search/expansion state
 *
 * ## Behavior Flow
 * 1. User expands "Chillers" and "Boilers" manually
 * 2. User types "pump" in search → All sections with "pump" auto-expand
 * 3. User manually collapses "Chillers" → Collapse persists (not re-expanded)
 * 4. User clears search → Returns to step 1 state (only "Chillers" and "Boilers" expanded)
 *
 * ## Implementation Details
 * Uses content-based comparison (via string key) to detect when search results change,
 * preventing unnecessary re-expansions when user manually toggles sections.
 *
 * State is stored per-chart using Maps keyed by currentChartId to prevent cross-chart
 * contamination when switching between charts.
 *
 * @param groupedDevices - Filtered device groups to display in accordion
 */
export const useAutoExpandSearch = (
  groupedDevices: SidebarAccordionItem<SelectionsDeviceGroup>[]
) => {
  const [searchTerm] = useAtom(SelectionState.searchTerm);
  const [expandedSectionIds, setExpandedSectionIds] = useAtom(SelectionState.expandedSectionIds);
  const currentChartId = useAtomValue(currentChartIdAtom);

  /**
   * Per-chart map storing the expansion state from before the user started searching.
   * Used to restore accordion to its pre-search state when search is cleared.
   * Key: chartId, Value: original expansion state for that chart
   */
  const originalExpansionMapRef = useRef<Map<string, string[]>>(new Map());

  /**
   * Per-chart map tracking the previous search term to detect search start/end transitions.
   * Key: chartId, Value: previous search term for that chart
   */
  const previousSearchTermMapRef = useRef<Map<string, string>>(new Map());

  /**
   * Mirrors current expandedSectionIds without adding it to effect dependencies.
   * Allows reading current state without triggering effects when it changes.
   */
  const expandedSectionIdsRef = useRef<string[]>(expandedSectionIds);

  /**
   * Per-chart map storing a string representation of the previous sections list.
   * Used for content-based comparison to detect actual result changes vs. re-renders.
   * Key: chartId, Value: comma-separated section IDs for that chart
   */
  const previousSectionsKeyMapRef = useRef<Map<string, string>>(new Map());

  // ============================================================================
  // EFFECT 1: Sync expandedSectionIds to ref
  // ============================================================================
  /**
   * Keeps the ref mirror in sync with actual state.
   * This allows later effects to read current value without depending on it.
   */
  useEffect(() => {
    expandedSectionIdsRef.current = expandedSectionIds;
  }, [expandedSectionIds]);

  // ============================================================================
  // MEMOIZED CALCULATION: Sections to expand
  // ============================================================================
  /**
   * Calculates which accordion sections should be expanded for current search results.
   * Memoized to only recalculate when search term or results actually change.
   *
   * Returns:
   * - Empty array if no search term
   * - Sorted array of section IDs (kind groups + devices within them)
   *
   * Why sorted? Ensures stable comparison for detecting content changes.
   */
  const sectionsToExpand = useMemo(() => {
    if (!currentChartId || !searchTerm) return [];

    const sections: string[] = [];
    groupedDevices.forEach((kindGroup) => {
      sections.push(kindGroup.value);
      kindGroup.data.devices.forEach((device: SidebarAccordionItem<SelectionsDevice>) => {
        sections.push(device.value);
      });
    });

    return sections.sort(); // Sort for stable comparison
  }, [currentChartId, searchTerm, groupedDevices]);

  // ============================================================================
  // EFFECT 2: Save/restore original expansion state (per-chart)
  // ============================================================================
  /**
   * Detects search start/end transitions and manages state preservation for the current chart.
   *
   * Search Start (empty → has value):
   * - Captures current expansion state to per-chart map
   * - Allows restoration when search ends
   *
   * Search End (has value → empty):
   * - Restores pre-search expansion state from per-chart map
   * - Clears the saved state for this chart
   *
   * During Search (has value → different value):
   * - Does nothing, preserves saved state
   *
   * Chart Switch:
   * - Each chart maintains its own saved state
   * - Switching charts doesn't affect other charts' saved states
   */
  useEffect(() => {
    if (!currentChartId) {
      return;
    }

    const previousSearchTerm = previousSearchTermMapRef.current.get(currentChartId) ?? '';
    const hadSearchTerm = previousSearchTerm.length > 0;
    const hasSearchTerm = searchTerm.length > 0;

    if (hasSearchTerm && !hadSearchTerm) {
      // Search just started - save current expansion state for this chart
      originalExpansionMapRef.current.set(currentChartId, [...expandedSectionIds]);
    } else if (!hasSearchTerm && hadSearchTerm) {
      // Search cleared - restore original expansion state for this chart
      const originalExpansion = originalExpansionMapRef.current.get(currentChartId);
      if (originalExpansion !== undefined) {
        setExpandedSectionIds(originalExpansion);
        originalExpansionMapRef.current.delete(currentChartId);
        // Also clear the sections key when search is cleared
        previousSectionsKeyMapRef.current.delete(currentChartId);
      }
    }

    // Update previous search term for this chart
    previousSearchTermMapRef.current.set(currentChartId, searchTerm);
  }, [searchTerm, expandedSectionIds, setExpandedSectionIds, currentChartId]);

  // ============================================================================
  // EFFECT 3: Auto-expand matching sections (per-chart)
  // ============================================================================
  /**
   * Expands accordion sections that match search results for the current chart.
   *
   * Key Innovation: Content-based comparison prevents re-running on manual toggles.
   * - Converts section IDs to comma-separated string for comparison
   * - Only expands when this string actually changes for the current chart
   * - Manual collapses don't change the string, so effect doesn't re-run
   *
   * Flow:
   * 1. User types "pump" → sectionsKey changes → Auto-expand
   * 2. User manually collapses "Pumps" → sectionsKey unchanged → No action
   * 3. User types "pump 1" → sectionsKey changes → Auto-expand new results
   *
   * Chart Switch:
   * - Each chart maintains its own sections key
   * - Switching back to a chart and searching again will auto-expand correctly
   *
   * Why use ref for currentExpanded?
   * - Avoids adding expandedSectionIds to dependencies
   * - Prevents effect from re-running when user manually toggles
   */
  useEffect(() => {
    if (!currentChartId) {
      return;
    }

    // Only auto-expand when there's an active search term
    if (!searchTerm || sectionsToExpand.length === 0) {
      return;
    }

    // Create a stable string representation of sections to expand
    const sectionsKey = sectionsToExpand.join(',');

    // Only run if the sections to expand have actually changed for this chart
    const previousSectionsKey = previousSectionsKeyMapRef.current.get(currentChartId) ?? '';
    if (sectionsKey === previousSectionsKey) {
      return;
    }

    // Update the sections key for this chart
    previousSectionsKeyMapRef.current.set(currentChartId, sectionsKey);

    // Merge new sections with existing (preserves manual expansions during search)
    // Use ref to get current value without adding it to dependencies
    const currentExpanded = expandedSectionIdsRef.current;
    const merged = Array.from(new Set([...currentExpanded, ...sectionsToExpand]));

    // Only update if something changed
    if (merged.length !== currentExpanded.length) {
      setExpandedSectionIds(merged);
    }
  }, [searchTerm, sectionsToExpand, setExpandedSectionIds, currentChartId]);
};
